import React from 'react';
import _ from 'lodash';
import moment from 'moment';
import { observer } from 'mobx-react';
import { defaultTemplate } from 'core/hoc';
import { BaasicButton, BaasicHeader, DatePicker } from 'core/components';
import BillingPaged from './BillingPaged';

const Select = ({ t, value, options, onChange, disabled }) => (
	<select
		className="input input--sml input--select"
		value={value}
		onChange={e => onChange(e.target.value)}
		disabled={disabled}
	>
		<option value="">{t('BILLING.SELECT')}</option>
		{_.map(options, o => (
			<option key={o.value} value={o.value}>
				{o.label}
			</option>
		))}
	</select>
);

const AssignCells = observer(({ t, viewStore, row }) => {
	if (!row.trackingNumber) {
		return (
			<React.Fragment>
				<td>-</td>
				<td>-</td>
			</React.Fragment>
		);
	}
	const { sellerId, carrierId } = viewStore.getAssignment(row);
	const hasSeller = sellerId && sellerId !== viewStore.notBillableValue;
	return (
		<React.Fragment>
			<td>
				<Select
					t={t}
					value={sellerId}
					options={[
						{ value: viewStore.notBillableValue, label: t('BILLING.NOTBILLABLEOPTION') },
						...viewStore.sellerOptions(row.carrier),
					]}
					onChange={value => viewStore.setAssignedSeller(row, value)}
					disabled={viewStore.isRunning}
				/>
			</td>
			<td>
				<Select
					t={t}
					value={carrierId}
					options={hasSeller ? viewStore.carrierOptions(sellerId, row.carrier) : []}
					onChange={value => viewStore.setAssignedCarrier(row, value)}
					disabled={viewStore.isRunning || !hasSeller}
				/>
			</td>
		</React.Fragment>
	);
});

// A box whose table can be shown or hidden from its header; actions are extra header buttons.
const CollapsibleBox = ({ t, title, isExpanded, onToggle, actions, children }) => (
	<div className="box--primary box--sml spc--bottom--sml">
		<div className="flex--primary spc--bottom--sml">
			<strong className="spc--right--med">{title}</strong>
			<BaasicButton
				className="btn btn--sml btn--ghost spc--right--med"
				type="button"
				icon={`fa ${isExpanded ? 'fa-chevron-up' : 'fa-chevron-down'} spc--left--nano`}
				// Despite the name, false puts the arrow after the label.
				iconAfter={false}
				label={isExpanded ? 'BILLING.HIDE' : 'BILLING.SHOW'}
				onClick={onToggle}
			/>
			{actions}
		</div>
		{isExpanded && children}
	</div>
);

// One page of a table plus its pager. mode: 'matched' (shows the upcharge), 'unmatched' (seller/carrier dropdowns
// instead of how it matched) or 'notBillable'.
const PagedMatchTable = ({ t, usd, page, mode, viewStore, table }) => (
	<BillingPaged t={t} page={page} onPage={p => viewStore.setPage(table, p)}>
		<MatchTable t={t} usd={usd} rows={page.rows} mode={mode} viewStore={viewStore} />
	</BillingPaged>
);

const MatchTable = observer(({ t, usd, rows, mode, viewStore }) => (
	<div className="billing__scroll">
		<table className="table table--secondary">
			<thead>
				<tr>
					<th className="border">{t('BILLING.SELLER')}</th>
					<th className="border">{t('BILLING.CARRIERACCOUNT')}</th>
					<th className="border">{t('BILLING.TRACKINGNUMBER')}</th>
					<th className="border">{t('BILLING.CARRIER')}</th>
					<th className="border">{t('BILLING.INVOICEDATE')}</th>
					<th className="border">{t('BILLING.CARRIERINVOICE')}</th>
					<th className="border type--right">{t('BILLING.NETCHARGE')}</th>
					{mode === 'matched' && <th className="border type--right">{t('BILLING.UPCHARGE')}</th>}
					{mode !== 'unmatched' && <th className="border">{t('BILLING.MATCHEDBY')}</th>}
					{mode !== 'unmatched' && <th className="border type--right">{t('BILLING.SCORE')}</th>}
					<th className="border">{t('BILLING.SHIPPERCOMPANY')}</th>
					<th className="border">{t('BILLING.REFERENCES')}</th>
					{mode !== 'unmatched' && <th className="border" />}
				</tr>
			</thead>
			<tbody>
				{_.map(rows, row => (
					<tr key={`${row.trackingNumber}|${row.carrier}|${row.sellerId}`}>
						{mode === 'unmatched' ? (
							<AssignCells t={t} viewStore={viewStore} row={row} />
						) : (
							<React.Fragment>
								<td>{row.sellerName || '-'}</td>
								<td>{row.carrierName || '-'}</td>
							</React.Fragment>
						)}
						<td>{row.trackingNumber || '-'}</td>
						<td>{row.carrier}</td>
						<td>{moment(row.invoiceDate).format('L')}</td>
						<td>{row.invoiceNumbers}</td>
						<td className="type--right">{usd(row.netCharge)}</td>
						{mode === 'matched' && (
							<td className="type--right">
								{usd(row.upcharge)}
								{row.chargesWithoutUpchargeCount > 0 && (
									<i
										className="type--color--warning-light fa fa-exclamation-circle spc--left--nano"
										title={t('BILLING.NOUPCHARGE', {
											count: row.chargesWithoutUpchargeCount,
											total: row.chargeCount,
										})}
									/>
								)}
							</td>
						)}
						{mode !== 'unmatched' && (
							<React.Fragment>
								<td>{row.matchedBy ? t(`BILLING.MATCHEDBYTYPES.${row.matchedBy}`) : '-'}</td>
								<td className="type--right">{row.matchScore != null ? row.matchScore.toFixed(2) : '-'}</td>
							</React.Fragment>
						)}
						<td>{row.shipperCompany || '-'}</td>
						<td>{row.references ? row.references.split('|').join(', ') : '-'}</td>
						{mode !== 'unmatched' && (
							<td>
								{viewStore.canUnmatch(row) && (
									<BaasicButton
										className="btn btn--sml btn--ghost"
										type="button"
										label="BILLING.UNMATCH"
										disabled={viewStore.isRunning}
										onClick={() => viewStore.unmatch(row)}
									/>
								)}
							</td>
						)}
					</tr>
				))}
			</tbody>
		</table>
	</div>
));

const BillingChargesTemplate = ({ viewStore, t }) => {
	const {
		form,
		format,
		today,
		rootStore,
		isRunning,
		currentStep,
		importedCount,
		lastImportRange,
		importCharges,
		refresh,
		matches,
		matchedPackages,
		unmatchedPackages,
		matchedPage,
		unmatchedPage,
		notBillablePage,
		isMatchedExpanded,
		toggleMatchedExpanded,
		isUnmatchedExpanded,
		toggleUnmatchedExpanded,
		notBillablePackages,
		isNotBillableExpanded,
		toggleNotBillableExpanded,
		readyAssignments,
		saveAssignments,
	} = viewStore;
	const isRangeEmpty = form.$('fromDate').value === '' || form.$('toDate').value === '';
	const usd = value => (value == null ? '-' : rootStore.usdFormatter.format(value));
	const fromDateValue = moment(form.$('fromDate').value, format, true);
	const toDateValue = moment(form.$('toDate').value, format, true);

	return (
		<React.Fragment>
			<BaasicHeader title="MAINMENU.BILLING.IMPORTCHARGES"></BaasicHeader>
			<div className="container">
				<div className="box--primary box--sml spc--bottom--sml">
					<p className="type--sml spc--bottom--sml">{t('BILLING.DESCRIPTION')}</p>
					<div className="flex--primary">
						<div className="spc--right--med">
							<DatePicker
								field={form.$('fromDate')}
								maxDate={toDateValue.isValid() ? toDateValue.toDate() : today}
								format={format}
								rootStore={rootStore}
								disabled={isRunning}
							/>
						</div>
						<div className="spc--right--med">
							<DatePicker
								field={form.$('toDate')}
								minDate={fromDateValue.isValid() ? fromDateValue.toDate() : undefined}
								maxDate={today}
								format={format}
								rootStore={rootStore}
								disabled={isRunning}
							/>
						</div>
						<BaasicButton
							className="btn btn--med btn--secondary spc--right--med"
							type="button"
							label="BILLING.IMPORT"
							disabled={isRunning || isRangeEmpty}
							onClick={importCharges}
						/>
						<BaasicButton
							className="btn btn--med btn--primary btn--primary--negative"
							type="button"
							icon="fa fa-redo spc--right--sml"
							label="BILLING.RELOAD"
							disabled={isRunning}
							onClick={refresh}
						/>
					</div>
				</div>

				{isRunning && (
					<div className="box--primary box--sml spc--bottom--sml flex--primary">
						<div className="spinner-inline-small"></div>
						<strong className="spc--left--sml">{t(currentStep)}</strong>
					</div>
				)}

				{importedCount != null && (
					<p className="type--sml spc--bottom--sml">
						{t('BILLING.IMPORTED', {
							count: importedCount,
							fromDate: lastImportRange.fromDateLabel,
							toDate: lastImportRange.toDateLabel,
						})}
					</p>
				)}

				{matches != null && (
					<React.Fragment>
						<CollapsibleBox
							t={t}
							title={t('BILLING.MATCHED', {
								count: matchedPackages.length,
								netCharge: usd(_.sumBy(matchedPackages, 'netCharge')),
								upcharge: usd(_.sumBy(matchedPackages, 'upcharge')),
							})}
							isExpanded={isMatchedExpanded}
							onToggle={toggleMatchedExpanded}
						>
							{matchedPackages.length === 0 ? (
								<p className="type--sml">{t('BILLING.NOMATCHED')}</p>
							) : (
								<PagedMatchTable
									t={t}
									usd={usd}
									page={matchedPage}
									mode="matched"
									table="matched"
									viewStore={viewStore}
								/>
							)}
						</CollapsibleBox>
						<CollapsibleBox
							t={t}
							title={t('BILLING.UNMATCHED', {
								count: unmatchedPackages.length,
								netCharge: usd(_.sumBy(unmatchedPackages, 'netCharge')),
							})}
							isExpanded={isUnmatchedExpanded}
							onToggle={toggleUnmatchedExpanded}
							actions={
								<BaasicButton
									className="btn btn--sml btn--primary"
									type="button"
									label={t('BILLING.CALCULATE', { count: readyAssignments.length })}
									disabled={isRunning || readyAssignments.length === 0}
									onClick={saveAssignments}
								/>
							}
						>
							{unmatchedPackages.length === 0 ? (
								<p className="type--sml">{t('BILLING.NOUNMATCHED')}</p>
							) : (
								<PagedMatchTable
									t={t}
									usd={usd}
									page={unmatchedPage}
									mode="unmatched"
									table="unmatched"
									viewStore={viewStore}
								/>
							)}
						</CollapsibleBox>
						{notBillablePackages.length > 0 && (
							<CollapsibleBox
								t={t}
								title={t('BILLING.NOTBILLABLE', {
									count: notBillablePackages.length,
									netCharge: usd(_.sumBy(notBillablePackages, 'netCharge')),
								})}
								isExpanded={isNotBillableExpanded}
								onToggle={toggleNotBillableExpanded}
							>
								<PagedMatchTable
									t={t}
									usd={usd}
									page={notBillablePage}
									mode="notBillable"
									table="notBillable"
									viewStore={viewStore}
								/>
							</CollapsibleBox>
						)}
					</React.Fragment>
				)}
			</div>
		</React.Fragment>
	);
};

export default defaultTemplate(BillingChargesTemplate);
